<template>
  <div class="queclink-tracker-statistics-live-block box p-4">
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-2">
      <b-skeleton v-if="entry.informationLoading" width="60px" />
      <p v-else class="title is-6 mb-0" :class="initialBatteryClass">
        {{
          aggregates.initialPercentage !== null ? `${aggregates.initialPercentage} %` : EMPTY_VALUE
        }}
      </p>
      <span v-if="entry.vehicleNumber" class="tag is-light">N°{{ entry.vehicleNumber }}</span>
    </div>

    <div class="mb-3">
      <p
        class="queclink-tracker-statistics-live-block__name is-size-7 has-text-weight-semibold"
        :title="entry.live.name"
      >
        <router-link
          target="_blank"
          :to="{ name: 'live.edit.informations', params: { id: entry.live.id, tab: 'review' } }"
        >
          {{ entry.live.name }}
        </router-link>
      </p>
      <p class="is-size-7 has-text-grey">{{ dates }}</p>
    </div>

    <div class="queclink-tracker-statistics-live-block__chart is-relative mb-4">
      <b-loading :active="entry.seriesLoading" :is-full-page="false" />
      <o-chart v-if="entry.series.length" :options="chartOptions" />
      <p v-else-if="!entry.seriesLoading" class="pt-5 is-size-7 has-text-grey has-text-centered">
        Aucune position sur ce live.
      </p>
    </div>

    <hr class="my-2" />

    <div v-if="entry.informationLoading">
      <b-skeleton />
      <b-skeleton width="70%" />
    </div>
    <div v-else>
      <div
        v-for="statistic in statistics"
        :key="statistic.label"
        class="is-flex is-align-items-center is-justify-content-space-between mb-1"
      >
        <p class="is-flex is-align-items-center is-size-7 has-text-grey">
          <b-icon class="mr-1" :icon="statistic.icon" size="is-small" />
          {{ statistic.label }}
        </p>
        <p class="is-size-7 has-text-weight-semibold">{{ statistic.value }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import OChart from '@components/Chart.vue'

const EMPTY_VALUE = '—'

const DATE_FORMAT = 'dd/MM/yyyy'

const INITIAL_BATTERY_TIERS = [
  { above: 90, className: 'has-text-green' },
  { above: 80, className: 'has-text-yellow' },
  { above: 70, className: 'has-text-orange' },
]

const AREA_ALPHA = '26'

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 60 * 60

const formatDuration = (seconds) => {
  const rounded = Math.round(seconds)

  if (rounded < SECONDS_PER_MINUTE) {
    return `${rounded} s`
  }

  if (rounded < SECONDS_PER_HOUR) {
    return `${Math.floor(rounded / SECONDS_PER_MINUTE)} min`
  }

  const hours = Math.floor(rounded / SECONDS_PER_HOUR)
  const minutes = Math.floor((rounded % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)

  return `${hours} h ${String(minutes).padStart(2, '0')}`
}

const MINI_CHART_OPTIONS = {
  animation: false,
  parsing: false,
  normalized: true,
  responsive: true,
  maintainAspectRatio: false,
  events: [],
  scales: {
    x: { type: 'time', display: false },
    y: { display: false, suggestedMin: 0, suggestedMax: 100 },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
}

export default {
  name: 'QueclinkTrackerStatisticsLiveBlock',

  components: { OChart },

  props: {
    entry: {
      type: Object,
      required: true,
      validator: (v) =>
        [
          'live',
          'vehicleNumber',
          'stages',
          'statistics',
          'aggregates',
          'series',
          'informationLoading',
          'seriesLoading',
        ].every((key) => key in v),
    },
    color: {
      type: String,
      required: true,
    },
  },

  computed: {
    dates() {
      return `${format(this.entry.live.startedAt, DATE_FORMAT)} → ${format(
        this.entry.live.endedAt,
        DATE_FORMAT,
      )}`
    },

    chartOptions() {
      return {
        type: 'line',
        options: MINI_CHART_OPTIONS,
        data: {
          datasets: this.entry.series.map((series) => ({
            data: series.points,
            fill: 'origin',
            borderColor: this.color,
            backgroundColor: `${this.color}${AREA_ALPHA}`,
            borderWidth: 1.5,
            borderJoinStyle: 'round',
            pointRadius: 0,
          })),
        },
      }
    },

    aggregates() {
      return this.entry.aggregates
    },

    initialBatteryClass() {
      const percentage = this.aggregates.initialPercentage
      if (percentage === null) {
        return 'has-text-grey'
      }

      return (
        INITIAL_BATTERY_TIERS.find((tier) => percentage > tier.above)?.className ?? 'has-text-red'
      )
    },

    statistics() {
      return [
        {
          label: 'Conso /h',
          icon: 'bolt',
          value: this.formatRate(this.aggregates.averageMotionConsumptionPerHour),
        },
        {
          label: 'Veille /h',
          icon: 'moon',
          value: this.formatRate(this.aggregates.averageIdleConsumptionPerHour),
        },
        {
          label: 'Recharges',
          icon: 'plug',
          value:
            this.aggregates.chargeCount !== null ? `${this.aggregates.chargeCount}` : EMPTY_VALUE,
        },
        {
          label: 'Intervalle moyen',
          icon: 'clock',
          value: this.formatInterval(this.aggregates.averageMotionIntervalSeconds),
        },
      ]
    },
  },

  created() {
    this.EMPTY_VALUE = EMPTY_VALUE
  },

  methods: {
    formatRate(percentagePerHour) {
      return percentagePerHour === null ? EMPTY_VALUE : `${percentagePerHour.toFixed(1)} %`
    },

    formatInterval(seconds) {
      return seconds === null ? EMPTY_VALUE : formatDuration(seconds)
    },
  },
}
</script>

<style lang="scss" scoped>
.queclink-tracker-statistics-live-block {
  height: 100%;

  &__name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__chart {
    height: 70px;
  }
}
</style>
